import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Message bar intent types matching Fluent UI MessageBar
 */
export type MessageBarIntent = 'info' | 'warning' | 'error' | 'success';

/**
 * Individual message bar item
 */
export interface MessageBarItem {
    id: string;
    intent: MessageBarIntent;
    title?: string;
    body: string;
    /** If true, the message bar cannot be dismissed by the user */
    persistent?: boolean;
}

/**
 * Message bar state
 */
export interface MessageBarState {
    items: MessageBarItem[];
}

export const initialMessageBarState: MessageBarState = {
    items: [],
};

const messageBarSlice = createSlice({
    name: 'messageBar',
    initialState: initialMessageBarState,
    reducers: {
        /**
         * Add a new message bar
         */
        addMessageBar: (state, action: PayloadAction<Omit<MessageBarItem, 'id'>>) => {
            const newItem: MessageBarItem = {
                ...action.payload,
                id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
            };
            state.items.push(newItem);
        },

        /**
         * Add a message bar with a specific ID (useful for singleton warnings)
         */
        addMessageBarWithId: (state, action: PayloadAction<MessageBarItem>) => {
            // Remove existing message with same ID first
            state.items = state.items.filter(item => item.id !== action.payload.id);
            state.items.push(action.payload);
        },

        /**
         * Remove a message bar by ID
         */
        removeMessageBar: (state, action: PayloadAction<string>) => {
            state.items = state.items.filter(item => item.id !== action.payload);
        },

        /**
         * Clear all message bars
         */
        clearAllMessageBars: (state) => {
            state.items = [];
        },

        /**
         * Clear message bars by intent type
         */
        clearMessageBarsByIntent: (state, action: PayloadAction<MessageBarIntent>) => {
            state.items = state.items.filter(item => item.intent !== action.payload);
        },
    },
});

export const {
    addMessageBar,
    addMessageBarWithId,
    removeMessageBar,
    clearAllMessageBars,
    clearMessageBarsByIntent,
} = messageBarSlice.actions;

export default messageBarSlice.reducer;

// Selector
export const selectCopilotMessageBarItems = (state: { copilotMessageBar: MessageBarState }) => state.copilotMessageBar.items;
